import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Search } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { fetchContacts } from '../../lib/dataProvider';
import { MOTIFS_EXCEPTION, TYPES_PIECE_LIBELLE, resoudreException, urlFichierPasserelle } from '../../lib/passerelle';
import { Carte, btn, btnLight, inputCls, labelCls } from '../cri/ui';

const ACTIONS = {
  tiers_a_affecter: 'affecter_tiers',
  doublon_probable: 'confirmer_correspondance',
  identite_contradictoire: 'piece_distincte',
  controles_en_echec: 'corriger_champ',
  type_non_reconnu: 'corriger_champ',
};

const CHAMPS_CORRIGEABLES = [
  ['numero', 'Numéro de pièce'],
  ['date_piece', 'Date de pièce'],
  ['total', 'Total (TND)'],
  ['devise', 'Devise'],
  ['type_piece', 'Nature de la pièce'],
];

const Exception = ({ exc, contacts, onMaj }) => {
  const [action, setAction] = useState(ACTIONS[exc.motif] ?? 'affecter_tiers');
  const [tiers, setTiers] = useState('');
  const [recherche, setRecherche] = useState('');
  const [champ, setChamp] = useState('numero');
  const [valeurChamp, setValeurChamp] = useState('');
  const n = exc.preuves?.normalise ?? {};

  const filtres = recherche.trim().length > 1
    ? contacts.filter((c) => (c.nom || '').toLowerCase().includes(recherche.trim().toLowerCase())).slice(0, 25)
    : [];

  const envoyer = async () => {
    let valeur = null;
    if (action === 'affecter_tiers' || action === 'confirmer_correspondance') valeur = tiers;
    if (action === 'corriger_champ') valeur = { champ, valeur: valeurChamp };
    const { error, data } = await resoudreException(exc._id, { action, valeur, auteur: 'gérant' });
    if (error) return toast.error(error);
    toast.success(data.decision === 'rattache' ? 'Justificatif rattaché à la pièce existante.' : 'Décision enregistrée — document remis en traitement.');
    onMaj();
  };

  return (
    <div data-testid={`passerelle-exception-${exc._id}`} className="border border-slate-200 bg-white">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-amber-50 px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-amber-900">{MOTIFS_EXCEPTION[exc.motif] ?? exc.motif}</p>
          <p className="text-[11px] text-amber-800">{exc.question}</p>
        </div>
        <a href={urlFichierPasserelle(exc.source_id)} target="_blank" rel="noreferrer" className={btnLight}>
          Voir la pièce
        </a>
      </header>

      <div className="grid gap-px bg-slate-100 sm:grid-cols-2">
        <div className="space-y-1 bg-white px-3 py-2 text-[11px] text-slate-600">
          <p>Nature : {TYPES_PIECE_LIBELLE[exc.type_piece] ?? exc.type_piece}</p>
          <p>Émetteur lu : {n.emetteur?.nom ?? '—'} {n.emetteur?.matricule_fiscal ? `(MF ${n.emetteur.matricule_fiscal})` : ''}</p>
          <p>Numéro : {n.numero_normalise ?? '—'} · Date : {n.date_piece ?? '—'}</p>
          <p>Total : {n.montants_millimes?.total != null ? `${fmt(n.montants_millimes.total / 1000, 2)} TND` : 'absent'}</p>
          {exc.detail?.ecarts?.length > 0 && <p className="text-amber-800">Écarts : {exc.detail.ecarts.join(' · ')}</p>}
          {exc.detail?.controles?.length > 0 && (
            <p className="text-amber-800">Contrôles en échec : {exc.detail.controles.map((c) => c.message).join(' · ')}</p>
          )}
        </div>

        <div className="space-y-3 bg-white px-3 py-2">
          <label className="block">
            <span className={labelCls}>Action métier</span>
            <select data-testid={`passerelle-action-${exc._id}`} value={action} onChange={(e) => setAction(e.target.value)} className={inputCls}>
              <option value="affecter_tiers">Affecter un tiers</option>
              <option value="confirmer_correspondance">Confirmer une correspondance</option>
              <option value="piece_distincte">Reconnaître une pièce distincte</option>
              <option value="corriger_champ">Corriger un champ</option>
            </select>
          </label>

          {action === 'affecter_tiers' && (
            <>
              <label className="block">
                <span className={labelCls}>Rechercher le tiers</span>
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-4 text-slate-400" />
                  <input
                    data-testid={`passerelle-recherche-tiers-${exc._id}`}
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    className={`${inputCls} pl-7`}
                    placeholder="Nom du fournisseur…"
                  />
                </div>
              </label>
              {(exc.candidats ?? []).length > 0 && (
                <div className="border border-slate-200">
                  <p className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500">Candidats proposés</p>
                  {exc.candidats.map((c) => (
                    <button
                      key={c.id ?? c.document_id}
                      type="button"
                      onClick={() => setTiers(c.id ?? c.document_id)}
                      className={`block w-full px-2 py-1.5 text-left text-xs ${tiers === (c.id ?? c.document_id) ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    >
                      {c.nom ?? c.document_id}
                    </button>
                  ))}
                </div>
              )}
              {filtres.length > 0 && (
                <div className="max-h-40 overflow-auto border border-slate-200">
                  {filtres.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`passerelle-tiers-${c.id}`}
                      onClick={() => setTiers(c.id)}
                      className={`block w-full px-2 py-1.5 text-left text-xs ${tiers === c.id ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    >
                      {c.nom}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {action === 'confirmer_correspondance' && (
            <div className="border border-slate-200">
              <p className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500">Pièces candidates</p>
              {(exc.candidats ?? []).map((c) => (
                <button
                  key={c.document_id}
                  type="button"
                  onClick={() => setTiers(c.document_id)}
                  className={`block w-full px-2 py-1.5 text-left text-xs ${tiers === c.document_id ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                >
                  {c.numero ?? c.document_id} · {c.total_millimes != null ? `${fmt(c.total_millimes / 1000, 2)} TND` : '—'}
                  {c.date_proche === false && ' · date éloignée'}
                </button>
              ))}
              {!(exc.candidats ?? []).length && <p className="px-2 py-2 text-xs text-slate-500">Aucun candidat.</p>}
            </div>
          )}

          {action === 'corriger_champ' && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block">
                <span className={labelCls}>Champ</span>
                <select value={champ} onChange={(e) => setChamp(e.target.value)} className={inputCls}>
                  {CHAMPS_CORRIGEABLES.map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>Valeur corrigée</span>
                <input data-testid={`passerelle-valeur-${exc._id}`} value={valeurChamp} onChange={(e) => setValeurChamp(e.target.value)} className={inputCls} />
              </label>
            </div>
          )}

          <button data-testid={`passerelle-resoudre-${exc._id}`} type="button" onClick={envoyer} className={btn}>
            <Check size={15} /> Enregistrer la décision
          </button>
        </div>
      </div>
    </div>
  );
};

export const ExceptionsPasserelle = ({ exceptions, onMaj }) => {
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    fetchContacts().then(setContacts);
  }, []);

  return (
    <Carte
      testid="passerelle-exceptions"
      titre="Exceptions à résoudre"
      sous="Chaque exception porte une action métier précise. Rien n’est deviné : aucune pièce n’est créée ni fusionnée sans décision."
    >
      {exceptions.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune exception ouverte : tous les documents acquis ont été traités automatiquement.</p>
      ) : (
        <div className="space-y-3">
          {exceptions.map((e) => (
            <Exception key={e._id} exc={e} contacts={contacts} onMaj={onMaj} />
          ))}
        </div>
      )}
    </Carte>
  );
};
